import { observer } from 'mobx-react';
import { CalendarCustom, ModalFooter, ReactNotifications } from '../../../components';
import { useStore } from '../../../core/utils/hook';
import yup from '../../../core/utils/yupValidate';
import { COURSE_PLAN_NOTIFY_TYPE, COURSE_PLAN_REMIND_TYPE, COURSE_PLAN_REMIND_TYPE_MAPPER, DATE_YMD_ISO, MSG } from '../../../core/configs/constants';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import classNames from 'classnames';

import './styles.scss';
import { flowResult } from 'mobx';
import { useMemo } from 'react';

const AddCoursePlanModal = observer(({ coursePeriod }) => {
    // store
    const { coursePlanStore: { createCoursePlan }, modalStore: { hide }, userStore: { getProfile } } = useStore();

    // form
    const validateSchema = yup.object().shape({
        planStartDate: yup.string().transform((value) => (value === null ? '' : value)).required(MSG['error.required']),
        planEndDate: yup.string().transform((value) => (value === null ? '' : value)).required(MSG['error.required']),
        targetTickets: yup.number().transform((value, originalValue) => originalValue === '' ? undefined : value).required(MSG['error.required']),
        isNotify: yup.boolean().optional(),
        notifyType: yup.mixed().when('isNotify', {
            is: true,
            then: yup.number()
                .transform((value, originalValue) => originalValue === '' ? undefined : value)
                .required(MSG['error.required']),
            otherwise: yup.string().nullable().notRequired()
        }),
        remindType: yup.mixed().when('isNotify', {
            is: true,
            then: yup.number()
                .transform((value, originalValue) => originalValue === '' ? undefined : value)
                .required(MSG['error.required']),
            otherwise: yup.string().nullable().notRequired()
        }),
        remindDateDuration: yup.mixed().when('remindType', {
            is: COURSE_PLAN_REMIND_TYPE.BEFORE_EXPIRED,
            then: yup.number().transform((value, originalValue) => originalValue === '' ? undefined : value).required(MSG['error.required']),
            otherwise: yup.string().nullable().notRequired()
        })
    });
        
    const coursePeriodEnd = useMemo(() => {
        const courseDate = new Date(coursePeriod);

        courseDate.setFullYear(courseDate.getFullYear() + 1);

        courseDate.setDate(courseDate.getDate() - 1);
        return courseDate;
    }, [coursePeriod]);
    
    const {
        register,
        handleSubmit,
        formState: {
            errors,
            isSubmitting
        },
        setValue,
        watch,
        getValues,
    } = useForm({resolver: yupResolver(validateSchema), mode: 'onChange', defaultValues: {
        remindType: (COURSE_PLAN_REMIND_TYPE.BEFORE_EXPIRED).toString(),
        remindDateDuration: '',
        targetTickets: ''
    }});

    const watchPlanStartDate = watch('planStartDate');
    const watchPlanEndDate = watch('planEndDate');
    const watchIsNotify = watch('isNotify');
    const watchRemindType = watch('remindType');
    const watchRemindDateDuration = watch('remindDateDuration');

    const maxValueRemindDuration = useMemo(() => {
        if (watchPlanStartDate && watchPlanEndDate) {
            return moment(watchPlanEndDate).diff(moment(watchPlanStartDate), 'days') + 1;
        }
        return 0;
    }, [watchPlanStartDate, watchPlanEndDate]);

    const remindNotifyDate = useMemo(() => {
        if (watchPlanStartDate && watchPlanEndDate) {
            if (watchRemindType === (COURSE_PLAN_REMIND_TYPE.ON_EXPIRED_DAY).toString()) {
                return moment(watchPlanEndDate).add(7, 'hours').format('※通知がYYYY年MM月DD日 HH時mm分に送信されます。');
            } else if (watchRemindType === (COURSE_PLAN_REMIND_TYPE.BEFORE_EXPIRED).toString() && watchRemindDateDuration) {
                return moment(watchPlanEndDate).subtract(watchRemindDateDuration, 'days').add(7, 'hours').format('※通知がYYYY年MM月DD日 HH時mm分に送信されます。')
            }
        }
        return null;
    }, [watchPlanStartDate, watchPlanEndDate, watchRemindType, watchRemindDateDuration]);

    // function
    const onSubmit = async (data) => {
        const { isNotify, notifyType, remindType, remindDateDuration, ...otherPayload } = data;
        const payload = {
            ...otherPayload,
            ...(isNotify ? data : {
                isNotify: false,
                notifyType: null,
                remindType: null,
                remindDateDuration: null
            })
        };
        const res = await flowResult(createCoursePlan(payload));
        if (res) {
            ReactNotifications('success', MSG['inform.success.create']);
            getProfile({ isHomePage: true });
            hide();
        }
    }

    const handleChangeRemindDateDuration = (e) => {
        const inputValue = e.target.value;

        const numericValue = Number(inputValue);
        
        if (inputValue === '' || (numericValue >= 1 && numericValue <= maxValueRemindDuration)) {
            setValue('remindDateDuration', inputValue, { shouldValidate: true });
        }
    }

    const handleChangeTargetTickets = (e) => {
        const inputValue = e.target.value;

        const numericValue = Number(inputValue);
        
        if (inputValue === '' || (numericValue >= 1)) {
            setValue('targetTickets', inputValue, { shouldValidate: true });
        }
    }

    return (
        <div>
            <form onSubmit={handleSubmit(onSubmit)} className='add-course-plan-modal'>
                <div className='form-data'>
                    <div className='plan-period row'>
                        <div className='col-4 title'>計画期間</div>
                        <div className='col-8 form row pd-0'>
                            <div className='col-5 pd-0 form-item'>
                                <CalendarCustom
                                    date={getValues('planStartDate')}
                                    minDate={new Date(coursePeriod)}
                                    maxDate={getValues('planEndDate') ? new Date(getValues('planEndDate')) : coursePeriodEnd}
                                    displayMode={'date'}
                                    onChange={date => {
                                        setValue('planStartDate', date ? moment(date).format(DATE_YMD_ISO) : null, { shouldValidate: true });
                                    }}
                                />
                                {
                                    errors.planStartDate &&
                                    <div className='text-danger fs-error mg-t-5 pd-0' style={{ fontSize: '11px' }}>
                                        {errors.planStartDate?.message}</div>
                                }
                            </div>
                            <div className='col-2 text-center flex-none form-item' style={{ marginTop: '8px' }}>~</div>
                            <div className='col-5 pd-0 form-item'>
                                <CalendarCustom
                                    date={getValues('planEndDate')}
                                    minDate={getValues('planStartDate') ? new Date(getValues('planStartDate')) : new Date(coursePeriod)}
                                    maxDate={coursePeriodEnd}
                                    displayMode={'date'}
                                    onChange={date => {
                                        setValue('planEndDate', date ? moment(date).format(DATE_YMD_ISO) : null, { shouldValidate: true });
                                    }}
                                />
                                {
                                    errors.planEndDate &&
                                    <div className='text-danger fs-error mg-t-5 pd-0' style={{ fontSize: '11px' }}>
                                        {errors.planEndDate?.message}</div>
                                }
                            </div>
                        </div>
                    </div>
                    {/* <div className='plan-period-error row'>
                        <div className='col-4 title'></div>
                        <div className='col-8 form row pd-0'>
                            <div className='col-5 pd-0'>
                                {
                                    errors.planStartDate &&
                                    <div className='text-danger fs-error mg-t-5 pd-0' style={{ fontSize: '11px' }}>
                                        {errors.planStartDate?.message}</div>
                                }
                            </div>
                            <div className='col-2 text-center'></div>
                            <div className='col-5 pd-0'>
                                {
                                    errors.planEndDate &&
                                    <div className='text-danger fs-error mg-t-5 pd-0' style={{ fontSize: '11px' }}>
                                        {errors.planEndDate?.message}</div>
                                }
                            </div>
                        </div>
                    </div> */}
                    <div className='plan-target row mg-t-25'>
                        <div className='col-4 title'>受講完了目標数</div>
                        <div className='col-8 form row align-items-center pd-0'>
                            <input
                                type="number"
                                value={getValues('targetTickets')}
                                onChange={(e) => handleChangeTargetTickets(e)}
                                className="col-5 no-spinner"
                            />
                            <div className='col-2 pd-0 font-bold'>講座</div>
                        </div>
                    </div>
                    <div className='plan-target-error row'>
                        <div className='col-4 title'></div>
                        <div className='col-8 form row align-items-center pd-0'>
                            {
                                errors.targetTickets &&
                                <div className='text-danger fs-error mg-t-5 pd-0' style={{ fontSize: '11px' }}>
                                    {errors.targetTickets?.message}</div>
                            }
                        </div>
                    </div>
                    <div className='plan-remind'>
                        <div className='plan-remind-status row align-items-center'>
                            <div className='col-4 title'>リマインダー設定</div>
                            <div className='col-8 form pd-0'>
                                <SwitchPrimitives.Root
                                    className='custom-switch'
                                    onCheckedChange={(checked) => setValue('isNotify', checked, { shouldValidate: true })}>
                                    <SwitchPrimitives.Thumb className='custom-toggle-thumb'/>
                                </SwitchPrimitives.Root>
                            </div>
                        </div>
                        {
                            watchIsNotify && (
                                <>
                                    <div className='plan-remind-option row'>
                                        <div className='plan-remind-option-notify col-5'>
                                            <select {...register('notifyType')} style={{ width: '100%' }}>
                                                {
                                                    Object.keys(COURSE_PLAN_NOTIFY_TYPE).map((key, index) => (
                                                        <option key={index} value={COURSE_PLAN_NOTIFY_TYPE[key].VALUE}>
                                                            {COURSE_PLAN_NOTIFY_TYPE[key].LABEL}
                                                        </option>
                                                    ))
                                                }
                                            </select>
                                        </div>
                                        <div className='plan-remind-option-remind col-7 d-flex flex-gap-15'>
                                            <div className={classNames(watchRemindType === (COURSE_PLAN_REMIND_TYPE.BEFORE_EXPIRED).toString()
                                                ? 'col-7' : 'col-12')}>
                                                <select {...register('remindType')} style={{ width: '100%' }}>
                                                    {
                                                        Object.values(COURSE_PLAN_REMIND_TYPE).map((value, index) => (
                                                            <option key={index} value={value}>{COURSE_PLAN_REMIND_TYPE_MAPPER[value]}</option>
                                                        ))
                                                    }
                                                </select>
                                            </div>
                                            {
                                                watchRemindType === (COURSE_PLAN_REMIND_TYPE.BEFORE_EXPIRED).toString() && (
                                                    <div className='col-4-5'>
                                                        <div className='d-flex align-items-center flex-gap-10'>
                                                            <input
                                                                type="number"
                                                                value={watchRemindDateDuration}
                                                                onChange={(e) => handleChangeRemindDateDuration(e)}
                                                                onKeyDown={(e) => {
                                                                    const invalidKeys = ['e', 'E', '+', '-', '.'];
                                                                    if (invalidKeys.includes(e.key)) {
                                                                        e.preventDefault();
                                                                    }
                                                                }}
                                                                disabled={!getValues('planStartDate') || !getValues('planEndDate')}
                                                                className="col-5 no-spinner flex-grow pd-lr-15"
                                                            />
                                                            <span>日</span>
                                                        </div>
                                                        {
                                                            errors.remindDateDuration &&
                                                            <div className='text-danger fs-error mg-t-5 pd-0' style={{ fontSize: '11px' }}>
                                                                {errors.remindDateDuration?.message}</div>
                                                        }
                                                    </div>
                                                )
                                            }
                                        </div>
                                    </div>
                                    {/* <div className='plan-remind-option-error row'>
                                        <div className='col-5'>
                                        </div>
                                        <div className='col-7 d-flex align-items-center flex-gap-5'>
                                            <div className={classNames(watchRemindType === (COURSE_PLAN_REMIND_TYPE.BEFORE_EXPIRED).toString()
                                                ? 'col-7' : 'col-12')}>
                                            </div>
                                            {
                                                watchRemindType === (COURSE_PLAN_REMIND_TYPE.BEFORE_EXPIRED).toString() && (
                                                    <div className='col-5 d-flex align-items-center flex-gap-10'>
                                                        {
                                                            errors.remindDateDuration &&
                                                            <div className='text-danger fs-error mg-t-5 pd-0' style={{ fontSize: '11px' }}>
                                                                {errors.remindDateDuration?.message}</div>
                                                        }
                                                    </div>
                                                )
                                            }
                                        </div>
                                    </div> */}
                                </>
                            )
                        }
                        {
                            remindNotifyDate && (
                                <div className='plan-remind-notify-date'>{remindNotifyDate}</div>
                            )
                        }
                    </div>
                </div>
                <div className='modal-footer'>
                    <button
                        type='button'
                        className='btn btn-default-1 cancel-button'
                        data-dismiss='modal'
                        onClick={() => hide()}
                    >
                        キャンセル
                    </button>
                    <button
                        type='submit'
                        disabled={isSubmitting}
                        className='btn btn-default-3'
                    >
                        保存
                    </button>
                </div>
            </form>
        </div>
    )
});

export default AddCoursePlanModal;